import { MeasurementSystem } from '@seddi/core';
import { DropdownMenu, EditableTitle, Icon, IconSize, Input } from '@seddi/ui-kit';
import { LogoTechnique } from '@seddi/scene';
import { type FC, type KeyboardEvent, type BaseSyntheticEvent, type SVGProps } from 'react';

import { DecreaseIcon, IncreaseIcon, WarningIcon } from '../../icons/GraphicIcons';
import type { StyleLogoRegion } from '../../api/styleAggregator';
import type { MockupLogo } from '../../types/mockupLogos';
import {
  AlignWrapper,
  Card,
  NameContainer,
  Row,
  RowLabel,
  ControlSide,
  InputRow,
  InputContainer,
  RegularLabel,
  ButtonContainer,
  IconContainer,
  SmallText,
} from './GraphicProperties.styles';
import {
  useGraphicProperties,
  TECHNIQUE_OPTIONS,
  type DropdownOption,
} from './GraphicProperties.hooks';

const DecreaseIconCast = DecreaseIcon as FC<SVGProps<SVGElement>>;
const IncreaseIconCast = IncreaseIcon as FC<SVGProps<SVGElement>>;

type Props = {
  logo: MockupLogo;
  regionOptions: DropdownOption[];
  editableRegions?: StyleLogoRegion[];
  blockBrandLogoRegions?: boolean;
  styleRegions: StyleLogoRegion[];
  units?: MeasurementSystem;
};

export const GraphicProperties: FC<Props> = ({
  logo,
  regionOptions = [{ id: 0, value: 'none', label: 'None' }],
  editableRegions = [],
  blockBrandLogoRegions = false,
  styleRegions = [],
  units,
}) => {
  const {
    customTechnique,
    handleCustomTechniqueBlur,
    handleNameBlur,
    handleRegionChange,
    handleSizeInputChange,
    handleTechniqueChange,
    inputValues,
    onInputFocus,
    onInputFocusOut,
    setCustomTechnique,
    scaleSize,
    showOutsideWarn,
    unitSuffix,
  } = useGraphicProperties({
    logo,
    regionOptions,
    styleRegions,
    editableRegions,
    blockBrandLogoRegions,
    units,
  });

  return (
    <Card data-properties-content>
      <NameContainer>
        <EditableTitle
          key={logo.id}
          value={logo.displayName || logo.name}
          onBlur={handleNameBlur}
          maxLength={256}
          showEditIconAlways
        />
      </NameContainer>

      <Row>
        <RowLabel>Placement:</RowLabel>
        <ControlSide>
          <DropdownMenu
            onChange={handleRegionChange}
            size="lg"
            menuPosition="outside"
            width="full"
            defaultValue={logo?.region || 'none'}
            options={regionOptions}
            maxHeight={260}
          />
        </ControlSide>
      </Row>

      {showOutsideWarn && (
        <Row>
          <ControlSide>
            <AlignWrapper>
              <Icon icon={WarningIcon as FC<SVGProps<SVGElement>>} size={IconSize.XL} />
              <SmallText>
                Logo outside of the imprint area, please check with your decorator to ensure it can
                be produced.
              </SmallText>
            </AlignWrapper>
          </ControlSide>
        </Row>
      )}

      <Row>
        <RowLabel>Size:</RowLabel>
        <ControlSide>
          <InputRow>
            <RegularLabel>W:</RegularLabel>
            <InputContainer>
              <Input
                type="number"
                value={inputValues.x}
                onChange={(e: BaseSyntheticEvent) => handleSizeInputChange('x', e.target.value)}
                onFocus={() => onInputFocus('x')}
                onBlur={() => onInputFocusOut('x')}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter') {
                    onInputFocusOut('x');
                    e.currentTarget.blur();
                  }
                }}
                min={0.1}
                max={1e10}
              />
            </InputContainer>
            <RegularLabel>{unitSuffix}</RegularLabel>
            <RegularLabel>H:</RegularLabel>
            <InputContainer>
              <Input
                type="number"
                value={inputValues.y}
                onChange={(e: BaseSyntheticEvent) => handleSizeInputChange('y', e.target.value)}
                onFocus={() => onInputFocus('y')}
                onBlur={() => onInputFocusOut('y')}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter') {
                    onInputFocusOut('y');
                    e.currentTarget.blur();
                  }
                }}
                min={0.1}
                max={1e10}
              />
            </InputContainer>
            <RegularLabel>{unitSuffix}</RegularLabel>
          </InputRow>

          <ButtonContainer>
            <IconContainer onClick={() => scaleSize('decrease')}>
              <Icon icon={DecreaseIconCast} size={IconSize.M} />
            </IconContainer>
            <IconContainer onClick={() => scaleSize('increase')}>
              <Icon icon={IncreaseIconCast} size={IconSize.M} />
            </IconContainer>
          </ButtonContainer>
        </ControlSide>
      </Row>

      <Row>
        <RowLabel>Technique:</RowLabel>
        <ControlSide>
          <DropdownMenu
            onChange={handleTechniqueChange}
            size="lg"
            menuPosition="outside"
            width="full"
            defaultValue={logo.technique}
            options={TECHNIQUE_OPTIONS}
            maxHeight={220}
          />
          {logo.technique === LogoTechnique.Custom && (
            <Input
              type="text"
              placeholder="Technique name"
              value={customTechnique}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setCustomTechnique(e.target.value);
              }}
              onBlur={handleCustomTechniqueBlur}
              maxLength={36}
            />
          )}
        </ControlSide>
      </Row>
    </Card>
  );
};
